import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Shield, CheckCircle2, ArrowRight, Star, MessageCircle, Mail, Clock, Zap, Award, DollarSign, Users, Loader2 } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';
import RegistrationPopup from '../components/RegistrationPopup';
import { getGeoLocation, submitLead } from '../utils/api';
import { processSteps } from '../mockData';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });

  const countryCodes = [
    { code: '+1', country: 'US/CA' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+52', country: 'Mexico' },
    { code: '+55', country: 'Brazil' },
    { code: '+7', country: 'Russia' },
    { code: '+27', country: 'South Africa' },
    { code: '+971', country: 'UAE' }
  ];

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const geoResult = await getGeoLocation();
      if (geoResult.success) {
        setGeoData(geoResult);
        // Auto-set country code based on location
        setFormData(prev => ({
          ...prev,
          countryCode: `+${geoResult.dialCode}`
        }));
      }
    };
    
    fetchGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.contactNumber,
        countryDialCode: parseInt(formData.countryCode.replace('+', '')),
        country: geoData?.countryCode || 'US',
        description: formData.message,
        ipAddress: geoData?.ipAddress || '',
        pageUrl: window.location.href,
        city: geoData?.city || '',
        state: geoData?.state || '',
        geoLocation: geoData?.fullResponse || null,
        packageTitle: '',
        packageAmount: 0
      };

      const result = await submitLead(leadData);

      if (result.success) {
        toast.success(result.message || 'Thank you! Redirecting...');
        setFormData({ name: '', email: '', countryCode: '+1', contactNumber: '', message: '' });
        
        // Redirect to application page with customerId
        setTimeout(() => {
          // navigate(`/application/step-one?customerid=${result.customerId}`);
          window.location.href = '/application/main?customerid=' + result.customerId;
        }, 1000);
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special validation for contact number - only numbers, max 10 digits
    if (name === 'contactNumber') {
      const numericValue = value.replace(/\D/g, ''); // Remove all non-digits
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const features = [
    { icon: Zap, title: '24-Hour Filing', desc: 'Fast application preparation and submission' },
    { icon: Award, title: '98% Success Rate', desc: 'Industry-leading approval rate' },
    { icon: Shield, title: 'Comprehensive Protection', desc: 'Full trademark registration coverage' },
    { icon: Users, title: 'Expert Attorneys', desc: '15+ years of experience' }
  ];

  const benefits = [
    'Exclusive nationwide rights to your mark',
    'Legal presumption of ownership',
    'Ability to sue in federal court',
    'Use the ® registered symbol',
    'Prevent others from using similar marks',
    'Foundation for international protection',
    'Valuable business asset',
    'Enhanced brand credibility'
  ];

  const testimonials = [
    {
      name: 'Jennifer Martinez',
      company: 'StyleHub Fashion',
      content: 'Amazing service! They registered our trademark quickly and kept us informed throughout the process. Highly recommend!',
      rating: 5
    },
    {
      name: 'David Thompson',
      company: 'Tech Innovations LLC',
      content: 'Professional, knowledgeable, and responsive. Made the trademark process simple and stress-free.',
      rating: 5
    },
    {
      name: 'Lisa Chen',
      company: 'Organic Beauty Co.',
      content: 'Best decision for our brand! The team was exceptional and our trademark was approved without any issues.',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'How long does trademark registration take?',
      answer: 'The process typically takes 8-12 months from filing to approval, though we file your application within 24 hours.'
    },
    {
      question: 'What can I trademark?',
      answer: 'You can trademark business names, logos, slogans, product names, and any distinctive mark that identifies your brand.'
    },
    {
      question: 'Do I need an attorney?',
      answer: 'While not required, working with an attorney significantly increases your approval chances and ensures proper protection.'
    },
    {
      question: 'What happens if my application is rejected?',
      answer: 'We respond to office actions and work with you to address any examiner concerns to maximize approval chances.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 md:py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/assets/img/brand/logo-dark.png" 
                alt="Trademark Upgrades Logo" 
                className="h-10 md:h-12 w-auto"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setIsRegistrationOpen(true)}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs md:text-sm px-3 md:px-4"
              >
                <span className="hidden sm:inline">Register My Trademark</span>
                <span className="sm:hidden">Register</span>
              </Button>
              <Button 
                onClick={() => window.openLiveChat && window.openLiveChat()}
                variant="outline"
                size="sm"
                className="border-orange-600 text-orange-600 hover:bg-orange-50 text-xs md:text-sm px-3 md:px-4"
              >
                <MessageCircle className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Chat Now</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Popup */}
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      <RegistrationPopup isOpen={isRegistrationOpen} onClose={() => setIsRegistrationOpen(false)} />

      {/* Hero Section */}
      <section className="relative bg-white py-12 md:py-20 lg:py-24 overflow-hidden">
        {/* Orange Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top Right Circle */}
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-100 rounded-full opacity-50"></div>
          {/* Bottom Left Circle */}
          <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-orange-50 rounded-full"></div>
          {/* Decorative Lines */}
          <div className="absolute top-1/4 right-1/4 w-64 h-1 bg-gradient-to-r from-transparent via-orange-300 to-transparent rotate-45"></div>
          <div className="absolute bottom-1/3 left-1/3 w-48 h-1 bg-gradient-to-r from-transparent via-orange-200 to-transparent -rotate-12"></div>
          {/* Small Orange Dots */}
          <div className="absolute top-1/3 left-1/4 w-3 h-3 bg-orange-400 rounded-full"></div>
          <div className="absolute top-2/3 right-1/3 w-2 h-2 bg-orange-300 rounded-full"></div>
          <div className="absolute bottom-1/4 left-2/3 w-4 h-4 bg-orange-200 rounded-full"></div>
        </div>
        
        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block mb-4">
                <span className="bg-orange-100 text-orange-700 text-xs md:text-sm font-semibold px-3 md:px-4 py-1.5 md:py-2 rounded-full border-2 border-orange-200">
                  ⭐ Rated 5.0 by 2,500+ Clients
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 leading-tight text-gray-900">
                Register Your Trademark in 24 Hours
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed">
                Protect your brand with expert trademark registration services. Fast, affordable, and hassle-free.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-8">
                <Button 
                  size="lg" 
                  onClick={() => setIsRegistrationOpen(true)}
                  className="bg-orange-600 text-white hover:bg-orange-700 text-sm md:text-base lg:text-lg px-6 md:px-8 w-full sm:w-auto shadow-lg"
                >
                  Register My Trademark <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                </Button>
                <Button 
                  size="lg" 
                  onClick={() => window.openLiveChat && window.openLiveChat()}
                  variant="outline" 
                  className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 text-sm md:text-base lg:text-lg px-6 md:px-8 w-full sm:w-auto"
                >
                  <MessageCircle className="mr-2 h-4 w-4 md:h-5 md:w-5" /> Talk to an Expert Now
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8">
                <div className="text-center sm:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-orange-600">10,000+</div>
                  <div className="text-sm md:text-base text-gray-600">Trademarks Filed</div>
                </div>
                <div className="text-center sm:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-orange-600">98%</div>
                  <div className="text-sm md:text-base text-gray-600">Success Rate</div>
                </div>
                {/* <div className="text-center sm:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-orange-600">$299</div>
                  <div className="text-sm md:text-base text-gray-600">Starting Price</div>
                </div> */}
              </div>
            </div>

            {/* Lead Form */}
            <Card className="border-0 shadow-2xl mt-8 lg:mt-0">
              <CardContent className="pt-6 md:pt-8 px-4 md:px-6">
                <div className="text-center mb-4 md:mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Start Your Registration Today</h3>
                  <p className="text-sm md:text-base text-gray-600">Get a free consultation with our trademark experts</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactNumber">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 w-24"
                      >
                        {countryCodes.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.code}
                          </option>
                        ))}
                      </select>
                      <Input
                        id="contactNumber"
                        name="contactNumber"
                        type="tel"
                        required
                        value={formData.contactNumber}
                        onChange={handleChange}
                        placeholder="1234567890"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        title="Please enter exactly 10 digits"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">What are you looking to protect?</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white" size="lg">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Get Free Consultation'
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to receive calls and messages about our services.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 md:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Why Choose Us?</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Fast, professional trademark registration services with industry-leading success rates
            </p>
          </div>
          <div className="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-8 flex md:flex-none overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 md:mx-0 md:px-0 mb-8 md:mb-12">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center snap-center flex-shrink-0 w-[75vw] md:w-auto bg-gray-50 p-6 rounded-lg">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm md:text-base text-gray-600">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12 lg:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Why Register Your Trademark?</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Protect your brand and gain exclusive legal rights
            </p>
          </div>
          <div className="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 flex md:flex-none overflow-x-auto gap-4 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 md:mx-0 md:px-0 mb-8 md:mb-12">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm snap-center flex-shrink-0 w-[85vw] md:w-auto">
                <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-br from-orange-600 via-orange-500 to-amber-600">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-white rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-amber-300 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-400 rounded-full blur-3xl"></div>
        </div>
        
        {/* Decorative Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-48 h-48 border-4 border-white/20 rounded-full"></div>
          <div className="absolute top-1/2 -left-16 w-32 h-32 border-4 border-white/20 rounded-full"></div>
          <div className="absolute bottom-16 right-1/4 w-24 h-24 bg-white/10 rotate-45"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight">
                Protect Your Brand Today
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-orange-50 leading-relaxed mb-6 md:mb-8">
                Don't leave your brand vulnerable to copycats and infringement. Our expert team has helped thousands of businesses secure their trademarks quickly and affordably. Start your trademark registration journey now and safeguard your intellectual property with confidence.
              </p>
            </div>

            {/* Right Buttons */}
            <div className="flex flex-col space-y-4 lg:items-end">
              <Button 
                size="lg" 
                onClick={() => setIsRegistrationOpen(true)}
                className="bg-white text-orange-600 hover:bg-gray-100 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all font-semibold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full lg:w-auto"
              >
                Register My Trademark <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                onClick={() => window.openLiveChat && window.openLiveChat()}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 shadow-xl hover:shadow-2xl transition-all font-semibold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full lg:w-auto backdrop-blur-sm"
              >
                <MessageCircle className="mr-2 h-5 w-5" /> Talk to an Expert Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12 lg:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Our Process</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Simple, transparent, and efficient trademark registration process
            </p>
          </div>
          
          {/* Desktop View */}
          <div className="hidden md:block md:max-w-4xl md:mx-auto relative">
            {/* Connecting Line */}
            <div className="absolute left-[60px] top-[80px] bottom-[80px] w-1 bg-gradient-to-b from-orange-600 via-orange-500 to-amber-600"></div>
            
            <div className="space-y-12">
              {processSteps.map((step) => (
                <div key={step.step} className="relative">
                  {/* Step Number Badge */}
                  <div className="absolute left-0 w-[120px] h-[120px]">
                    <div className="relative w-full h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl rotate-6 opacity-20"></div>
                      <div className="relative bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl w-full h-full flex items-center justify-center shadow-xl">
                        <span className="text-4xl font-bold text-white">{step.step}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Card */}
                  <div className="ml-[160px] bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-orange-100 hover:border-orange-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600 text-base leading-relaxed">{step.description}</p>
                      </div>
                      <div className="ml-6 flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                          <CheckCircle2 className="h-8 w-8 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Mobile View */}
          <div className="md:hidden flex overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4">
            {processSteps.map((step) => (
              <div key={step.step} className="snap-center flex-shrink-0 w-[85vw]">
                <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-3xl font-bold text-white">{step.step}</span>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-base">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the package that's right for your business
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Essentials */}
            <Card className="border-2 border-gray-200 h-[580px] flex flex-col">
              <CardContent className="pt-8 flex flex-col h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Essentials</h3>
                <div className="text-3xl font-bold text-orange-600 mb-3">$45</div>
                <p className="text-sm text-gray-600 mb-4 h-[40px]">Great for small businesses covering multiple product or service classes.</p>
                
                <div className="flex-1 overflow-y-auto mb-4 pr-2" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Federal Filing Fee</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Review</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Filing</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Basic Search</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">State E-Filing</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Secured</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <p className="text-xs text-gray-500 italic mb-4">(Govt. filing fee not included)</p>
                  <Button 
                    onClick={() => setIsRegistrationOpen(true)}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Register My Trademark
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced */}
            <Card className="border-2 border-gray-200 h-[580px] flex flex-col">
              <CardContent className="pt-8 flex flex-col h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Enhanced</h3>
                <div className="text-3xl font-bold text-orange-600 mb-3">$149</div>
                <p className="text-sm text-gray-600 mb-4 h-[40px]">Great for small businesses covering multiple product or service classes.</p>
                
                <div className="flex-1 overflow-y-auto mb-4 pr-2" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Federal Filing Fee</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Review</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Filing</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Direct Hit Search</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Alert</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Secured</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">High Satisfaction Rate</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Refusal Risk Meter</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <p className="text-xs text-gray-500 italic mb-4">(Govt. filing fee not included)</p>
                  <Button 
                    onClick={() => setIsRegistrationOpen(true)}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Register My Trademark
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Deluxe (Recommended) */}
            <Card className="border-2 border-orange-600 shadow-xl relative h-[580px] flex flex-col">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
                  Recommended
                </span>
              </div>
              <CardContent className="pt-8 flex flex-col h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Deluxe</h3>
                <div className="text-3xl font-bold text-orange-600 mb-3">$299</div>
                <p className="text-sm text-gray-600 mb-4 h-[40px]">For brands needing extra support, including help with USPTO office actions.</p>
                
                <div className="flex-1 overflow-y-auto mb-4 pr-2" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Federal Filing Fee</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Review</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Filing</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Direct Hit Search</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Alert</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Secured</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">High Satisfaction Rate</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Refusal Risk Meter</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Monitoring</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Complete Documentation</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Digital File</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Dedicated Case Manager</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <p className="text-xs text-gray-500 italic mb-4">(Govt. filing fee not included)</p>
                  <Button 
                    onClick={() => setIsChatOpen(true)}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Register My Trademark
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Platinum */}
            <Card className="border-2 border-gray-200 h-[580px] flex flex-col">
              <CardContent className="pt-8 flex flex-col h-full">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Platinum</h3>
                <div className="text-3xl font-bold text-orange-600 mb-3">$349</div>
                <p className="text-sm text-gray-600 mb-4 h-[40px]">All-inclusive plan with monitoring, renewals, and full legal assistance.</p>
                
                <div className="flex-1 overflow-y-auto mb-4 pr-2" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Federal Filing Fee</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Review</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Case Filing</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Direct Hit Search</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Alert</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Secured</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">High Satisfaction Rate</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Refusal Risk Meter</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Trademark Monitoring</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Complete Documentation</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Digital File</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Dedicated Case Manager</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">High Approval Rate</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Comprehensive Trademark Search Report (Federal & State)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">High Priority Service</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">Cease & Desist Letter</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <p className="text-xs text-gray-500 italic mb-4">(Govt. filing fee not included)</p>
                  <Button 
                    onClick={() => setIsRegistrationOpen(true)}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Register My Trademark
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* CTA Box */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-orange-600 to-amber-600 border-0 shadow-2xl">
              <CardContent className="pt-12 pb-12 px-8 flex flex-col justify-center items-center text-center">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6">
                  <Shield className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
                  Need Help Choosing?
                </h3>
                <p className="text-base md:text-lg lg:text-xl text-orange-100 mb-8 max-w-2xl">
                  Our trademark experts are here to help you select the perfect package for your business needs. Get a free consultation today!
                </p>
                <div className="space-y-3 md:space-y-4 mb-8 text-left max-w-md">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-white flex-shrink-0" />
                    <span className="text-sm md:text-base text-white">Free expert consultation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-white flex-shrink-0" />
                    <span className="text-sm md:text-base text-white">Personalized package recommendation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6 text-white flex-shrink-0" />
                    <span className="text-sm md:text-base text-white">No obligation, no pressure</span>
                  </div>
                </div>
                <Button 
                  size="lg"
                  onClick={() => window.openLiveChat && window.openLiveChat()}
                  className="bg-white text-orange-600 hover:bg-gray-100 shadow-xl text-base md:text-lg px-6 md:px-8 py-4 md:py-6 h-auto"
                >
                  <MessageCircle className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                  Talk to an Expert Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12 lg:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">What Our Clients Say</h2>
          </div>
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 md:mx-0 md:px-0">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 border-gray-200 snap-center flex-shrink-0 w-[85vw] md:w-auto">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              size="lg" 
              onClick={() => setIsRegistrationOpen(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Register My Trademark
            </Button>
            <Button 
              size="lg" 
              onClick={() => window.openLiveChat && window.openLiveChat()}
              variant="outline"
              className="border-orange-600 text-orange-600 hover:bg-orange-50"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Trusted by Businesses Nationwide</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join thousands of successful businesses that have protected their brands with us
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">10,000+</div>
              <div className="text-gray-600">Trademarks Filed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">98%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">24h</div>
              <div className="text-gray-600">Filing Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">50+</div>
              <div className="text-gray-600">Expert Team</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Protect Your Brand Today
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join thousands of businesses that trust us with their trademark registration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => setIsRegistrationOpen(true)}
              className="bg-white text-orange-600 hover:bg-gray-100"
            >
              Register My Trademark <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              onClick={() => window.openLiveChat && window.openLiveChat()}
              variant="outline" 
              className="border-white text-white hover:bg-white/10"
            >
              <MessageCircle className="mr-2 h-5 w-5" /> Talk to an Expert Now
            </Button>
          </div>
          <div className="mt-8 flex items-center justify-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Available 24/7</span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5" />
              <span>info@trademarkupgrades.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © 2025 Trademark Upgrades. All rights reserved.
            </p>
            <div className="flex items-center space-x-3">
              <a 
                href="/privacy-policy"
                className="text-sm text-gray-400 hover:text-orange-400 transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <a 
                href="/terms-of-service"
                className="text-sm text-gray-400 hover:text-orange-400 transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
